import { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { Share2 } from "lucide-react";
import { api } from "../../lib/api";

const RANGES = [
  { id: "7d",  label: "7 ימים" },
  { id: "30d", label: "30 ימים" },
  { id: "90d", label: "90 ימים" },
  { id: "all", label: "הכל" },
];

// Per-source colour palette — picks brand-accurate accents where
// possible (WhatsApp green, Telegram blue, Facebook navy, etc.) and
// falls back to the Torahya gold for unknown / generic buckets.
const SOURCE_COLOR = {
  whatsapp:  "#25D366",
  telegram:  "#26A5E4",
  facebook:  "#1877F2",
  instagram: "#E1306C",
  twitter:   "#1E2A3A",
  email:     "#B8893B",
  search:    "#7E8DA0",
  direct:    "#94B49F",
  other:     "#C2A878",
};

export default function ClickSourcesChart() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .adminClickSources({ range })
      .then((res) => {
        if (cancelled) return;
        setData(res.items || []);
        setTotal(res.total || 0);
      })
      .catch(() => {
        if (!cancelled) {
          setData([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [range]);

  return (
    <div
      className="bg-card/40 rounded-md border border-gold/25 p-5"
      data-testid="click-sources-chart"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-heading text-sm tracking-[0.2em] uppercase text-gold flex items-center gap-2">
            <Share2 className="h-4 w-4" strokeWidth={1.5} />
            מקורות תנועה
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            סך {total.toLocaleString("he-IL")} קליקים על קישורי שיתוף
          </p>
        </div>
        <div className="flex gap-1" data-testid="click-sources-range-toggle">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              data-testid={`click-sources-range-${r.id}`}
              className={`px-2.5 py-1 text-xs rounded-sm transition ${
                range === r.id
                  ? "bg-gold/15 text-gold border border-gold/30"
                  : "text-muted-foreground border border-transparent hover:text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-[260px]" data-testid="click-sources-canvas">
        {loading ? (
          <div className="h-full grid place-items-center text-xs text-muted-foreground">
            טוען נתונים…
          </div>
        ) : total === 0 ? (
          <div className="h-full grid place-items-center text-center px-4">
            <div>
              <p className="text-sm text-muted-foreground">
                עדיין אין קליקים בטווח זה.
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                כשמשתמשים ילחצו על קישור שיתוף, נציג כאן מאיפה הם הגיעו.
              </p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 5, right: 24, left: 8, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(0,0,0,0.06)"
                horizontal={false}
              />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: "currentColor" }}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="label"
                tick={{ fontSize: 12, fill: "currentColor" }}
                width={80}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                cursor={{ fill: "rgba(184,137,59,0.08)" }}
                contentStyle={{
                  background: "var(--card)",
                  border: "1px solid rgba(184,137,59,0.4)",
                  borderRadius: 4,
                  fontSize: 12,
                  direction: "rtl",
                }}
                formatter={(value, _name, props) => {
                  const pct = props?.payload?.percentage ?? 0;
                  return [`${value} (${pct}%)`, "קליקים"];
                }}
              />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={26}>
                {data.map((row) => (
                  <Cell
                    key={row.source}
                    fill={SOURCE_COLOR[row.source] || "#B8893B"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
